import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchRecentDailySummaries } from '../firebase';
import { computePersonalBaseline } from '../engine/metrics';

async function fetchSuggestion(metricsContext, baseline, history = []) {
  // Build baseline context string for Level 3/4 personalisation
  let baselineContext = '';
  if (baseline && baseline.daysOfData >= 2) {
    const attnDiff = baseline.avgAttention && metricsContext.attentionScore != null
      ? metricsContext.attentionScore - baseline.avgAttention : null;
    const perclosDiff = baseline.avgPerclos && metricsContext.perclos != null
      ? ((metricsContext.perclos - baseline.avgPerclos) * 100).toFixed(1) : null;

    baselineContext = `
Personal baseline (last ${baseline.daysOfData} days average):
- Typical attention score: ${baseline.avgAttention ?? '—'}/100
- Current vs baseline attention: ${attnDiff != null ? (attnDiff >= 0 ? '+' : '') + attnDiff + ' points' : '—'}
- Typical PERCLOS: ${baseline.avgPerclos != null ? (baseline.avgPerclos * 100).toFixed(1) + '%' : '—'}
- Current vs baseline PERCLOS: ${perclosDiff != null ? (perclosDiff >= 0 ? '+' : '') + perclosDiff + '%' : '—'}
- Typical blink rate: ${baseline.avgBlinkRate ?? '—'}/min
- Typical yawns per session: ${baseline.avgYawns ?? '—'}
- Typical flow minutes: ${baseline.avgFlowMins ?? '—'} min`;
  }

  const response = await fetch('/api/coach', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'llama-3.1-8b-instant',
      max_tokens: 350,
      messages: [
        {
          role: 'system',
          content: `You are Moodscope's wellness coach — warm, brief, and practical.
You receive live biometric data AND the user's personal historical baseline.
Use the baseline to give personalised, pattern-aware advice (not generic tips).

Rules:
- Be warm and human, not clinical. Use "you" not "the user".
- SHORT: 1 headline (max 8 words) + 2-3 sentences of context.
- If baseline data is available, reference how current readings compare to the user's norm. Example: "Your attention is 20 points below your usual level" or "Your fatigue is higher than normal for this time of day."
- If current state is poor AND has been building (high PERCLOS, low attention for a while), acknowledge the trend and recommend a specific action — not just a break, but what kind (walk, water, eyes closed, task switch).
- If current state is great, compare to baseline positively. "You are performing above your usual level."
- Level 4 insight: if the user is in a low-focus state, suggest what KIND of work suits their current state (light tasks, async messages, admin) vs what to avoid (deep work, decisions).
- Never mention AI or that you are an assistant.
- Respond ONLY with valid JSON (no markdown): {"headline": "...", "detail": "...", "type": "rest|focus|energy|posture|great|warning"}`
        },
        {
          role: 'user',
          content: `Current biometrics (last few minutes):
- Attention score: ${metricsContext.attentionScore ?? '—'}/100
- Emotion: ${metricsContext.emotion ?? 'unknown'}
- Energy level: ${metricsContext.energy ?? 'unknown'}
- Blink rate: ${metricsContext.blinkRate !== null && metricsContext.blinkRate !== undefined ? Number(metricsContext.blinkRate).toFixed(1) + '/min' : '—'} (normal: 12-20)
- Blink duration: ${metricsContext.blinkDurationAvg ? metricsContext.blinkDurationAvg + 'ms' : '—'} (normal: 100-200ms)
- PERCLOS: ${metricsContext.perclos !== null && metricsContext.perclos !== undefined ? (metricsContext.perclos * 100).toFixed(1) + '%' : '—'} (above 10% = tired)
- Yawns this session: ${metricsContext.yawnCount ?? 0}
- Focus percentage: ${metricsContext.focusPct !== null && metricsContext.focusPct !== undefined ? metricsContext.focusPct + '%' : '—'}
- Session duration: ${metricsContext.sessionMinutes ?? 0} minutes
${baselineContext}

${baseline ? 'Use the baseline comparison to make your suggestion personal and specific.' : 'No historical baseline yet — give a general suggestion based on current readings.'}
${history.length > 0 ? `
Previous suggestions already given today (do NOT repeat these, give something different):
${history.map((s, i) => `${i+1}. "${s.headline}"`).join('\n')}` : ''}`
        }
      ]
    })
  });

  if (!response.ok) throw new Error(`Coach API ${response.status}`);
  const data = await response.json();
  const text = data.choices?.[0]?.message?.content || '';
  try {
    return JSON.parse(text.replace(/```json|```/g, '').trim());
  } catch(e) {
    return { headline: 'Looking good — keep it up', detail: 'Your metrics are within a healthy range.', type: 'great' };
  }
}

// ── Background notification helper ────────────────────────────────────────
let _notifTitleTimer = null;
let _swRegistration  = null;

// Register service worker once
async function registerSW() {
  if (!('serviceWorker' in navigator)) return;
  try {
    _swRegistration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
  } catch(e) {
    console.warn('SW registration failed:', e);
  }
}
registerSW();

async function requestNotificationPermission() {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

async function notifyUser(suggestion) {
  const headline = suggestion?.headline || 'New tip from your AI coach';
  const detail   = suggestion?.detail   || '';

  // Tab title flash — always, regardless of visibility
  const orig = document.title;
  if (_notifTitleTimer) clearTimeout(_notifTitleTimer);
  document.title = '💡 ' + headline + ' — Moodscope';
  _notifTitleTimer = setTimeout(() => { document.title = orig; }, 12000);

  // Only send notification when tab is not visible
  if (document.visibilityState === 'visible') return;
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  try {
    // Use service worker for reliable background notifications
    const reg = _swRegistration || await navigator.serviceWorker.ready;
    if (reg?.active) {
      reg.active.postMessage({ type: 'COACH_NOTIFICATION', headline, detail });
      return;
    }
  } catch(e) {}

  // Fallback: direct Notification API
  try {
    const n = new Notification('Moodscope Coach 🤖', {
      body: headline, tag: 'moodscope-coach', requireInteraction: false,
    });
    n.onclick = () => { window.focus(); n.close(); };
    setTimeout(() => n.close(), 10000);
  } catch(e) {}
}

const TYPE_STYLES = {
  rest:    { icon: '😮‍💨', color: '#0a9688', bg: 'rgba(12,184,160,0.06)', border: 'rgba(12,184,160,0.25)' },
  focus:   { icon: '🎯', color: '#0a9688', bg: 'rgba(12,184,160,0.06)', border: 'rgba(12,184,160,0.25)' },
  energy:  { icon: '⚡', color: '#d97706', bg: 'rgba(245,158,11,0.06)', border: 'rgba(245,158,11,0.25)' },
  posture: { icon: '🧘', color: '#7c3aed', bg: 'rgba(139,92,246,0.06)', border: 'rgba(139,92,246,0.25)' },
  great:   { icon: '✨', color: '#0a9688', bg: 'rgba(12,184,160,0.06)', border: 'rgba(12,184,160,0.25)' },
  warning: { icon: '⚠️', color: '#d97706', bg: 'rgba(245,158,11,0.06)', border: 'rgba(245,158,11,0.25)' },
};

const AUTO_MS    = 15 * 60 * 1000;  // auto-refresh every 15 mins
const COOLDOWN_MS = 30 * 60 * 1000; // manual refresh once per 30 mins

function fmt(ms) {
  const s = Math.ceil(ms / 1000);
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
}

export function LiveSuggestions({ metrics, tracking, sessionMinutes, user }) {
  // Restore from localStorage on mount
  const [suggestion,  setSuggestion]  = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('moodscope_coach_v1') || 'null');
      if (saved?.date === new Date().toDateString()) return saved.suggestion || null;
    } catch(e) {}
    return null;
  });
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [lastUpdated, setLastUpdated] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('moodscope_coach_v1') || 'null');
      if (saved?.date === new Date().toDateString() && saved.lastUpdated) return new Date(saved.lastUpdated);
    } catch(e) {}
    return null;
  });
  const [history, setHistory] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('moodscope_coach_v1') || 'null');
      if (saved?.date === new Date().toDateString()) return saved.history || [];
    } catch(e) {}
    return [];
  });
  // ── Change 5: Restore cooldown from localStorage ─────────────────────────
  const [lastManual, setLastManual] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('moodscope_coach_cooldown_v1') || 'null');
      if (saved?.date === new Date().toDateString()) return saved.lastManual || null;
    } catch(e) {}
    return null;
  });
  const [cooldown,    setCooldown]    = useState(0);
  const [baseline,    setBaseline]    = useState(null);

  const timerRef   = useRef(null);
  const metricsRef = useRef({ ...metrics, sessionMinutes });
  useEffect(() => { metricsRef.current = { ...metrics, sessionMinutes }; }, [metrics, sessionMinutes]);

  // Fetch personal baseline from Firebase when user is available
  useEffect(() => {
    if (!user) return;
    fetchRecentDailySummaries(user.uid, 7)
      .then(summaries => {
        if (summaries.length >= 2) {
          setBaseline(computePersonalBaseline(summaries));
        }
      })
      .catch(() => {});
  }, [user]);

  // Tick the cooldown counter every second
  useEffect(() => {
    if (!lastManual) return;
    const tick = setInterval(() => {
      const left = COOLDOWN_MS - (Date.now() - lastManual);
      if (left <= 0) { setCooldown(0); clearInterval(tick); }
      else setCooldown(left);
    }, 1000);
    setCooldown(COOLDOWN_MS - (Date.now() - lastManual));
    return () => clearInterval(tick);
  }, [lastManual]);

  const doFetch = useCallback(async () => {
    if (!tracking) return;
    setLoading(true); setError(null);
    try {
      const s = await fetchSuggestion(metricsRef.current, baseline, history);
      setSuggestion(s);
      setLastUpdated(new Date());
      setHistory(prev => {
        const next = [s, ...prev].slice(0, 3);
        try {
          localStorage.setItem('moodscope_coach_v1', JSON.stringify({
            date:        new Date().toDateString(),
            suggestion:  s,
            history:     next,
            lastUpdated: new Date().toISOString(),
          }));
        } catch(e) {}
        return next;
      });

      // Notify user even when tab is in background
      notifyUser(s);
    } catch(e) {
      setError(e.message);
    }
    setLoading(false);
  }, [tracking]);

  const handleManualRefresh = useCallback(() => {
    if (cooldown > 0 || loading) return;
    const now = Date.now();
    setLastManual(now);
    // Persist cooldown so it survives refresh
    try {
      localStorage.setItem('moodscope_coach_cooldown_v1', JSON.stringify({
        date:        new Date().toDateString(),
        lastManual:  now,
      }));
    } catch(e) {}
    doFetch();
  }, [cooldown, loading, doFetch]);

  // Auto-refresh loop
  useEffect(() => {
    if (!tracking) { clearTimeout(timerRef.current); clearInterval(timerRef.current); return; }
    const init = setTimeout(doFetch, 8000);
    timerRef.current = setInterval(doFetch, AUTO_MS);
    return () => { clearTimeout(init); clearInterval(timerRef.current); };
  }, [tracking, doFetch]);

  if (!tracking) return null;

  const style = suggestion ? (TYPE_STYLES[suggestion.type] || TYPE_STYLES.great) : null;
  const onCooldown = cooldown > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '0.62rem', letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--muted)' }}>AI Coach</div>
        <button
          onClick={handleManualRefresh}
          disabled={onCooldown || loading}
          title={onCooldown ? `Next refresh available in ${fmt(cooldown)}` : 'Get a new suggestion'}
          style={{
            background: 'transparent', border: 'none',
            color: onCooldown ? 'var(--text)' : 'var(--muted)',
            fontSize: '0.65rem', fontFamily: "'Nunito',sans-serif",
            cursor: onCooldown || loading ? 'not-allowed' : 'pointer',
            opacity: onCooldown ? 0.75 : loading ? 0.6 : 1,
            display: 'flex', alignItems: 'center', gap: 4,
            transition: 'all 0.2s',
          }}
        >
          {loading
            ? <span style={{ display:'inline-block', width:10, height:10, border:'1.5px solid var(--border)', borderTopColor:'var(--accent)', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
            : '↻'
          }
          {onCooldown
            ? <span>refresh <span style={{ color:'var(--accent)', fontWeight:600 }}>{fmt(cooldown)}</span></span>
            : 'refresh'}
        </button>
      </div>

      {/* Card */}
      <div style={{ background: style?.bg ?? 'var(--surface)', border: `1px solid ${style?.border ?? 'var(--border)'}`, borderRadius:14, padding:'14px 16px', minHeight:80, transition:'all 0.4s ease', position:'relative', overflow:'hidden' }}>
        {loading && !suggestion && (
          <div style={{ display:'flex', alignItems:'center', gap:10, color:'var(--muted)' }}>
            <span style={{ display:'inline-block', width:14, height:14, border:'2px solid var(--border)', borderTopColor:'var(--accent)', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
            <span style={{ fontSize:'0.72rem', fontFamily:"'Nunito',sans-serif" }}>Analysing your session…</span>
          </div>
        )}
        {!loading && !suggestion && !error && (
          <div style={{ color:'var(--muted)', fontSize:'0.72rem', fontFamily:"'Nunito',sans-serif" }}>First suggestion in a few seconds…</div>
        )}
        {error && !suggestion && (
          <div style={{ color:'var(--muted)', fontSize:'0.68rem', fontFamily:"'Nunito',sans-serif", opacity:0.7 }}>
            Coach unavailable right now.
            <button onClick={handleManualRefresh} style={{ marginLeft:8, background:'transparent', border:'none', color:'var(--accent)', fontSize:'0.65rem', cursor:'pointer', textDecoration:'underline' }}>retry</button>
          </div>
        )}
        {suggestion && (
          <div style={{ animation:'fadeIn 0.4s ease' }}>
            <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
              <span style={{ fontSize:'1.3rem', lineHeight:1, flexShrink:0 }}>{style?.icon}</span>
              <div>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:'0.95rem', color:style?.color, lineHeight:1.3, marginBottom:6 }}>{suggestion.headline}</div>
                <div style={{ fontSize:'0.71rem', color:'var(--text)', lineHeight:1.55, opacity:0.85, fontFamily:"'Nunito',sans-serif" }}>{suggestion.detail}</div>
              </div>
            </div>
            {lastUpdated && (
              <div style={{ marginTop:10, fontSize:'0.57rem', color:'var(--muted)', opacity:0.6 }}>
                Updated {lastUpdated.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}
              </div>
            )}
          </div>
        )}
        {loading && suggestion && (
          <div style={{ position:'absolute', inset:0, background:'rgba(255,255,255,0.6)', display:'flex', alignItems:'center', justifyContent:'center', borderRadius:14 }}>
            <span style={{ display:'inline-block', width:18, height:18, border:'2px solid var(--border)', borderTopColor:'var(--accent)', borderRadius:'50%', animation:'spin 0.7s linear infinite' }} />
          </div>
        )}
      </div>

      {/* History */}
      {history.length > 1 && (
        <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
          <div style={{ fontSize:'0.58rem', color:'var(--muted)', opacity:0.5, textTransform:'uppercase', letterSpacing:'0.1em' }}>Earlier</div>
          {history.slice(1).map((s, i) => {
            const st = TYPE_STYLES[s.type] || TYPE_STYLES.great;
            return (
              <div key={i} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'8px 12px', display:'flex', gap:8, alignItems:'flex-start', opacity:0.55 }}>
                <span style={{ fontSize:'0.9rem', flexShrink:0 }}>{st.icon}</span>
                <div style={{ fontSize:'0.65rem', color:'var(--muted)', fontFamily:"'Nunito',sans-serif", lineHeight:1.4 }}>{s.headline}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
